import { Formats, Map } from 'features/maps/types'
import { generateHashId } from '@carto/kepler.gl/utils'
import { ProtoDataset } from '@carto/kepler.gl/actions'
import { SourceProps } from '@carto/react-api'
import {
  LegendEntry,
  MapConfigForAgent,
  SpatialDataType,
  MapDatasetEnriched,
  AttributeStats,
  KeplerLayerClasses,
  PopupSettings,
  AttributeType
} from 'features/builder/types'
import { RasterMetadata, RasterMetadataBand } from '@carto/api-client'
import { DataExplorerTable, SchemaField } from 'features/data-explorer/types'
import { MapDataset, MapType } from 'features/maps/types'
import { NoSpatialIndexType, NoSpatialIndex, SpatialIndex, SpatialIndexColumn } from '..'
import { Account } from 'features/account/types'
import { checkAccountTableQueryLimits } from 'features/common/utils/limits'
import {
  BUILDER_MIN_ZOOM_DISCRETE,
  DEFAULT_GEOM_COLUMN,
  DEFAULT_GEO_COLUMN,
  TILEJSON_FORMAT_TYPES
} from '../ui/builderConstants'
import { ProviderType } from 'features/connections'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from 'app/state/store'
import { Layer } from '@carto/kepler.gl/layers'
import { KeplerLayer, KeplerTable, LayerType, LayerTypes, TilingScheme, Widget, WidgetType } from '../types'
import { FieldSchema } from '../../common/utils/tableUtils'
import { clamp } from 'features/common/utils/clamp'
import { getStore } from 'app/state/storeRegistry'
import { FeatureFlag } from 'app/types'
import { getFlag } from 'features/account/state/accountSlice'
import { isGeomField } from '../../common/utils/isGeomField'
import { _FilterTypes as FilterTypes } from '@carto/react-core'
import { Filter, SourceFilters } from '@carto/react-redux'

import { getColumnAliasForAggregationExp } from './spatialIndexUtils'
import { isRasterBlockField } from 'features/common/utils/isRasterBlockField'
import { getLayerFeatureType, LayerFeatureType } from '../utils/layerType'
import { legendEntriesWithCustomSettings } from './legends'
import { getSpatialDataSettingsFromSchema, isSchemeAggregated } from '../utils/spatialDataSettings'
import {
  parseGeoColumnSafe,
  formatGeoColumn,
  isDynamicAggregatedPointDataset,
  getSourceSpatialDataType
} from '../utils/geoColumn'
import { TilejsonResult } from '@carto/api-client'

export type Dataset = KeplerTable

export function getAttributesFromTilesetDataset(
  dataset: Omit<Dataset, 'fields' | 'allData' | 'allIndexes'>
): AttributeStats[] {
  return dataset.metadata?.tilestats?.attributes || []
}

export function getAttributeFromTilesetDataset(
  dataset: Omit<Dataset, 'fields' | 'allData' | 'allIndexes'>,
  column: string
) {
  return getAttributesFromTilesetDataset(dataset).find((a) => a.attribute === column)
}

export function getHexColumnStatsFromTilesetDataset(dataset: KeplerTable, column: string): AttributeStats | undefined {
  if (dataset.metadata?.sourceType === MapType.tileset) {
    return dataset.metadata?.tilestats?.attributes?.find((a) => a.attribute === column)
  }
  return dataset.metadata?.hexColumnStats?.[column]
}

export function attributeHasStats(attribute: AttributeStats | undefined) {
  if (!attribute) {
    return false
  }

  const type = attribute.type.toLocaleLowerCase()

  if (type === 'string' || type === 'boolean') {
    return Object.prototype.hasOwnProperty.call(attribute, 'categories')
  }
  if (type === 'number') {
    return (
      Object.prototype.hasOwnProperty.call(attribute, 'min') &&
      Object.prototype.hasOwnProperty.call(attribute, 'max') &&
      Object.prototype.hasOwnProperty.call(attribute, 'quantiles')
    )
  }

  return false
}

export function getGeometryFromTilesetDataset(dataset: Dataset) {
  return dataset.metadata?.tilestats?.geometry
}

export function getBoundsFromTilesetDataset(dataset: Dataset) {
  return dataset.metadata?.bounds
}

export function getTilejsonFromTilesetDataset(dataset: Dataset): TilejsonResult | null | undefined {
  return dataset.metadata?.tilejson
}

export function setTilejsonFromTilesetDataset(dataset: Dataset, tilejson: TilejsonResult | null | undefined) {
  dataset.metadata.tilejson = tilejson
}

export function setSchemaFromTilesetDataset(dataset: Dataset, schema: SchemaField[] | undefined) {
  dataset.metadata.schema = schema
}

export function getZoomRangeFromTilesetDataset(dataset: Dataset) {
  const tilejson = getTilejsonFromTilesetDataset(dataset)
  if (dataset.metadata?.sourceType === MapType.raster) {
    return [BUILDER_MIN_ZOOM_DISCRETE, tilejson?.maxzoom]
  }
  return [tilejson?.minzoom, tilejson?.maxzoom]
}

export function getDataResolutionFromTilesetDataset(dataset: Dataset): number | undefined {
  return dataset.metadata?.dataresolution ?? undefined
}

export function getSpatialFiltersOptions(keplerDataset: KeplerTable) {
  const { spatialDataColumn, spatialDataType } = parseGeoColumnSafe(
    keplerDataset.metadata?.geoColumn || DEFAULT_GEOM_COLUMN
  )
  const forceFilterByGeometry = isDynamicAggregatedPointDataset(keplerDataset)
  return {
    spatialDataColumn,
    spatialDataType: forceFilterByGeometry ? SpatialDataType.Geo : spatialDataType
  }
}

export function getAggregationResLevelFromTilesetDataset(dataset: Dataset): number | null | undefined {
  return dataset.metadata?.aggregationResLevel
}

export function setAggregationResLevelFromTilesetDataset(dataset: Dataset, aggregationResLevel: number) {
  dataset.metadata.aggregationResLevel = aggregationResLevel
}

export function getFieldsTypesFromTilesetDataset(dataset: Dataset): Record<string, AttributeType> | undefined {
  return dataset.metadata?.vector_layers?.[0]?.fields
}

export function getSchemeFromTilesetDataset(dataset: Dataset | MapDatasetEnriched): TilingScheme | undefined {
  return dataset.metadata?.scheme
}

export function getProviderFromDataset(dataset: Dataset) {
  return dataset.metadata?.provider
}

export function getDatasetType(dataset: Dataset | ProtoDataset) {
  return dataset.metadata?.type
}

export function getDatasetSourceType(dataset: Dataset | ProtoDataset) {
  return dataset.metadata?.sourceType
}

export function getgeoColumnFromTilesetDataset(dataset: Dataset) {
  return dataset.metadata?.geoColumn
}

export function isStaticSpatialIndexDataset(dataset: Dataset | MapDatasetEnriched) {
  const type = getDatasetSourceType(dataset)
  const scheme = getSchemeFromTilesetDataset(dataset)
  return type === MapType.tileset && scheme && isSchemeAggregated(scheme)
}

export function getKeplerFieldFromDatasetScheme(dataset: Dataset | MapDatasetEnriched) {
  const scheme = getSchemeFromTilesetDataset(dataset)
  return { id: scheme, name: scheme, displayName: scheme, type: undefined, analyzerType: undefined }
}

export function findFirstSuitableLayerConfigByDataset({
  dataset,
  layerClasses,
  label
}: {
  dataset: KeplerTable
  layerClasses: KeplerLayerClasses
  label?: string
}) {
  return findPossibleLayerConfigsByDataset({ dataset, layerClasses, label })[0]
}

export function findPossibleLayerConfigsByDataset({
  dataset,
  layerClasses,
  label
}: {
  dataset: KeplerTable
  layerClasses: KeplerLayerClasses
  label?: string
}) {
  const layersTypeByPriority: LayerType[] = [
    LayerTypes.Unknown,
    ...TILEJSON_FORMAT_TYPES,
    LayerTypes.GeoJSON,
    LayerTypes.S2,
    LayerTypes.HexagonId,
    LayerTypes.Point
  ]
  const allLayerTypes = Object.keys(layerClasses) as LayerType[]
  const priorityLayers = layersTypeByPriority.filter((l) => allLayerTypes.includes(l))
  const restOfLayerTypes = allLayerTypes.filter((l) => layersTypeByPriority.indexOf(l) === -1)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any[] = []

  for (const layerType of priorityLayers.concat(restOfLayerTypes)) {
    const layerConfig =
      typeof layerClasses[layerType]?.findDefaultLayerProps === 'function'
        ? layerClasses[layerType].findDefaultLayerProps(dataset, [])
        : { props: [] }

    const props = Array.isArray(layerConfig) ? layerConfig : layerConfig.props || []

    if (props.length > 0) {
      result.push(getLayerConfigCommonProps(dataset, layerType, props, label))
    }
  }

  return result
}

export function getLayerConfigByLayerType({
  layerType,
  dataset,
  layerClasses
}: {
  layerType: LayerType
  dataset: KeplerTable
  layerClasses: KeplerLayerClasses
}) {
  const result = layerClasses[layerType]?.findDefaultLayerProps(dataset, [])
  const props = Array.isArray(result) ? result : result?.props || []

  if (props.length > 0) {
    return getLayerConfigCommonProps(dataset, layerType, props)
  }

  return null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getLayerConfigCommonProps(dataset: any, layerType: LayerType, props: any[], label?: string) {
  const result = {
    id: generateHashId(),
    type: layerType,
    config: {
      ...props[0],
      dataId: dataset.id,
      ...(!!label && { label }),
      columnsIndexed: props[0].columns
    }
  }

  result.config.columns = Object.keys(result.config.columns).reduce((acc, current) => {
    const field = dataset.fields[result.config.columns[current]['fieldIdx']]
    if (field) {
      acc[current] = field.name
    }
    return acc
  }, {} as Record<string, string>)

  return result
}

export function getGeoColumnFromTable(table: DataExplorerTable | undefined) {
  if (!table) return {}

  let geoColumn: string | undefined = table?.geomField
  let hasSpatialIndex = false

  /*
    a) new backend endpoints are gonna start sending geomField information also for H3 / Quadbin tables
    in the same format as current one generated at front (improved logic)
    -- The only implementation to leave in code after merging --
  */
  if (geoColumn) {
    hasSpatialIndex = Boolean(getSpatialIndexFromGeoColumn(geoColumn))
  }

  /*
    b) current implementation: even if we don't receive geoColumn, we verify schema to see if
    a field can be a SpatialIndex column.
    -- Implementation to be removed after landing backend changes --
  */
  if (!geoColumn && table.schema) {
    const spatialDataSettings = getSpatialDataSettingsFromSchema(table.schema)
    if (spatialDataSettings) {
      geoColumn = formatGeoColumn(spatialDataSettings)
      hasSpatialIndex = spatialDataSettings.hasSpatialIndex
    }
  }

  return { geoColumn, hasSpatialIndex }
}

type TableDatasetSettings = {
  type: MapType
  format: Formats
  source: string
  geoColumn?: string
  hasSpatialIndex?: boolean
  spatialIndex?: SpatialIndex | null
  nrows: number
}

export function getDatasetSettingsForTable({
  table,
  account
}: {
  table: DataExplorerTable
  account: Account | Record<string, never>
}): TableDatasetSettings {
  if (table.type === MapType.raster) {
    const type = MapType.raster
    const format = Formats.RASTER
    const source = table.id
    const hasSpatialIndex = false
    const nrows = 0
    return { type, format, source, hasSpatialIndex, nrows }
  } else {
    const type = MapType.table
    let format = Formats.JSON
    const source = table?.id
    const nrows = table?.nrows
    const dataLimits = checkAccountTableQueryLimits(table, account)
    const { geoColumn: guessedGeoColumn, hasSpatialIndex } = getGeoColumnFromTable(table)
    const spatialIndex = hasSpatialIndex ? getSpatialIndexFromGeoColumn(guessedGeoColumn) : null

    const documentModeAvailable = account.documentModeAvailable
    let geoColumn = guessedGeoColumn
    if (!geoColumn) {
      format = Formats.TILEJSON
      geoColumn = DEFAULT_GEOM_COLUMN
    } else if (!documentModeAvailable || dataLimits || hasSpatialIndex || table?.isSample) {
      format = Formats.TILEJSON
    }

    return { type, format, source, geoColumn, hasSpatialIndex, nrows, spatialIndex }
  }
}

export function getFormatForQuerySources({
  provider,
  hasSpatialIndex
}: {
  provider: ProviderType
  hasSpatialIndex: boolean
}) {
  if ([ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST].includes(provider) && !hasSpatialIndex) {
    return Formats.JSON
  }
  return Formats.TILEJSON
}

export function shouldLoadAsDynamicTiling({ type, format }: Pick<MapDataset, 'type' | 'format'>) {
  return (type === MapType.table || type === MapType.query) && format === Formats.TILEJSON
}

export function isUnknownSpatialSettingsDataset(dataset: KeplerTable) {
  const type = getDatasetType(dataset)
  const tilejson = getTilejsonFromTilesetDataset(dataset)

  return type === MapType.tileset && !tilejson
}

export function isDynamicSpatialIndexDataset(dataset: Dataset | Pick<MapDataset, 'type' | 'format' | 'geoColumn'>) {
  if ('metadata' in dataset) {
    const type = getDatasetSourceType(dataset)
    const { tilejson, geoColumn } = dataset.metadata
    const format = tilejson ? Formats.TILEJSON : type === MapType.raster ? Formats.RASTER : Formats.JSON
    return isDynamicSpatialIndexDataset({ type, format, geoColumn })
  }

  const { type, format, geoColumn } = dataset
  const spatialIndex = geoColumn && getSpatialIndexFromGeoColumn(geoColumn)
  return Boolean(shouldLoadAsDynamicTiling({ type, format }) && spatialIndex)
}

export function hasSchemaGeomField(schema: FieldSchema[]) {
  return schema.some((f) => isGeomField(f.type) || getSpatialIndexFromGeoColumn(f.name))
}

export function hasSchemaBlockField(schema: FieldSchema[]) {
  return schema.some((f) => isRasterBlockField(f))
}

export function getSpatialIndexFromGeoColumn(geoColumn: string) {
  const spatialIndexToSearch = geoColumn.split(':')[0]

  for (const index of Object.values(SpatialIndex)) {
    if (SpatialIndexColumn[index].includes(spatialIndexToSearch)) {
      return index
    }
  }
  return null
}

export function getSpatialIndexFromTable(table: DataExplorerTable | undefined) {
  const { geoColumn, hasSpatialIndex } = getGeoColumnFromTable(table)
  return hasSpatialIndex && Boolean(geoColumn) ? getSpatialIndexFromGeoColumn(`${geoColumn}`) : null
}

export function getColumnNameFromGeoColumn(geoColumn: string | null | undefined) {
  if (!geoColumn) {
    return geoColumn
  }
  const parts = geoColumn.split(':')
  return parts.length === 1 ? parts[0] : parts.length === 2 ? parts[1] : null
}

/**
 * Modify `geo_column` specifier in form `geoColumn` or `spatialIndex:geoColumn` with spatial index.
 *
 * Possible return types:
 *  - `spatialIndex:spatialIndex` - if SI is specified, but source geometry column is unknown
 *  - `spatialIndex:column`
 *  - `column` - if no SI is specified
 */
export function getGeoColumnSpecifier(
  spatialIndex?: SpatialIndex | NoSpatialIndexType | null,
  oldGeoColumnSpecifier?: string | null
) {
  const columnName = (oldGeoColumnSpecifier && getColumnNameFromGeoColumn(oldGeoColumnSpecifier)) || ''

  if (!spatialIndex || spatialIndex === NoSpatialIndex) {
    return columnName || DEFAULT_GEO_COLUMN
  }
  return `${spatialIndex}:${columnName || spatialIndex}`
}

export function getLayerTypeChangeIncompatibleSettings(
  layers: KeplerLayer[],
  popupSettings: PopupSettings,
  widgets: Widget[],
  layerId: string
): string[] {
  const result: string[] = []
  const updatedLayer = layers.find((l) => l.id === layerId)
  if (!layerId || !updatedLayer) {
    return result
  }

  const datasetId = updatedLayer?.config?.dataId
  const allAffectedLayers = layers.filter((layer) => layer.config.dataId === datasetId)
  const timeSeriesWidgetsWithActiveControls = widgets.filter(
    (widget) => widget.type === WidgetType.TIMESERIES && widget.dataSource === datasetId && widget.showControls
  )

  for (const layer of allAffectedLayers) {
    const label = layer.config.label
    if (layer !== updatedLayer) {
      result.push(`${label}`)
    }

    if (popupSettings?.layers[layer.id]?.click || popupSettings?.layers[layer.id]?.hover) {
      result.push(`${label} popups`)
    }

    if (timeSeriesWidgetsWithActiveControls.some((widget) => widget.dataSource === layer.config.dataId)) {
      result.push('Animation control in Time Series widget')
    }
  }

  return result
}

export function isLayerTypeCompatibleWithQuery(type: LayerType) {
  return TILEJSON_FORMAT_TYPES.includes(type)
}

export const getConvertDatasetToQueryIncompatibleSettings = createSelector(
  [(state: RootState) => state.keplerGl.map?.visState?.layers, (_: unknown, datasetId: string) => datasetId],
  (layers: KeplerLayer[], datasetId) => {
    return layers
      .filter((layer) => layer.config.dataId === datasetId && !isLayerTypeCompatibleWithQuery(layer.type))
      .map((layer) => `${layer.config.label} style`)
  }
)

export function isTilesetDroppingFeatures(dataset: Dataset, zoom: number) {
  const { fractionDroppedPerZoom, maxZoom, minZoom } = dataset.metadata
  if (!fractionDroppedPerZoom?.length) {
    return false
  }

  const roundedZoom = Math.round(zoom)
  const clampedZoom = clamp(roundedZoom, minZoom || 0, maxZoom || 20)

  const targetPercent = fractionDroppedPerZoom[clampedZoom]
  return !!targetPercent && targetPercent > 0
}

export function isServerSideMapFilteringSupported({
  source,
  format = Formats.JSON
}: {
  source: SourceProps
  format?: Formats
}) {
  // @ts-expect-error TODO(c4r): Migrating types.
  return format !== Formats.JSON && (source.type === MapType.table || source.type === MapType.query)
}

export const widgetTypeToFilterType: Record<WidgetType, FilterTypes | null> = {
  [WidgetType.CATEGORY]: FilterTypes.IN,
  [WidgetType.PIE]: FilterTypes.IN,
  [WidgetType.HISTOGRAM]: FilterTypes.BETWEEN,
  [WidgetType.RANGE]: FilterTypes.BETWEEN,
  [WidgetType.TIMESERIES]: null, // TODO: Support time series filters.
  [WidgetType.TABLE]: null,
  [WidgetType.FORMULA]: null
}

export function getFilterByWidget(widget: Widget, dataset: MapDataset, filters: Record<string, SourceFilters>) {
  const sourceFilters = filters[widget.dataSource] as SourceFilters
  const filterType = widgetTypeToFilterType[widget.type]
  if (!widget.isValid || !filterType || !sourceFilters) return null

  const columnAlias = getColumnAliasByWidget(widget, dataset)
  if (!sourceFilters[columnAlias]) return null

  const filter = sourceFilters[columnAlias][filterType]
  if (!filter || filter.owner !== widget.id) return null

  return (filter as Filter) || null
}

export function getColumnAliasByWidget(widget: Widget, dataset: MapDataset) {
  // NOTE: getColumnAliasByWidget() is called by setDefaultMapConfig() during map initialization
  // when datasets are not yet in app state. Do not look up MapDataset with select() here.
  const isDynamicSpatialIndex = isDynamicSpatialIndexDataset(dataset)

  if (!widget.column) {
    return ''
  }

  if (!isDynamicSpatialIndex) {
    return widget.column
  }

  if (widget.spatialIndexAggregation && dataset.providerId) {
    return getColumnAliasForAggregationExp(widget.column, widget.spatialIndexAggregation, dataset.providerId)
  }

  return ''
}

export function getMapConfigForAgent({
  map,
  currentMapDatasets,
  layers
}: {
  map: Map
  currentMapDatasets: MapDataset[]
  layers: Layer[]
}): MapConfigForAgent | undefined {
  if (!map?.keplerMapConfig?.config) return
  const { widgets = [], mapState, visState, sqlParameters } = map.keplerMapConfig.config
  const { splitMaps } = visState
  const layerLegendSettings = map.keplerMapConfig.config.legendSettings?.layers
  const mapSettings = map.keplerMapConfig.config.mapSettings

  const getLegend = (legendEntry: LegendEntry[]) => {
    return legendEntry.map((entry) => {
      const {
        visualChannel,
        widgetProps: { basedOn, colors, labels, customMarkers }
      } = entry
      return {
        property: visualChannel,
        ...(basedOn && { basedOn }),
        ...(colors && { colors }),
        ...(labels && { labels }),
        ...(customMarkers && { icons: customMarkers })
      }
    })
  }

  const cleanedLayers = layers.map((layer) => {
    const {
      id,
      config: { label, dataId, isVisible }
    } = layer
    const featureType = LayerFeatureType[getLayerFeatureType(layer as KeplerLayer)]?.toLowerCase()
    const legendSettings = layerLegendSettings?.[id]
    const legend = getLegend(legendEntriesWithCustomSettings(layer as KeplerLayer, legendSettings))
    return {
      id,
      type: featureType,
      label,
      dataId,
      isVisible,
      legend
    }
  })

  const datasets = currentMapDatasets.map(({ id, providerId }) => ({
    id,
    provider: providerId
  }))

  const parsedWidgets = widgets.map((w) => ({
    id: w.id,
    label: w.title,
    ...(w.note && { note: w.note }),
    dataSource: w.dataSource,
    type: w.type,
    column: w.column,
    ...(w.operation && { operation: w.operation }),
    ...('operationColumn' in w && { operationColumn: w.operationColumn }),
    global: w.global,
    viewport: !w.global
  }))

  const splitMap = splitMaps?.length > 0
  let layerVisibilityBySplitMap

  if (splitMap) {
    layerVisibilityBySplitMap = {
      leftMap: Object.entries(splitMaps[0].layers).map(([layerId, isVisible]) => ({ layerId, isVisible })),
      rightMap: Object.entries(splitMaps[1].layers).map(([layerId, isVisible]) => ({ layerId, isVisible }))
    }
  }

  return {
    title: map.title || '',
    description: map.description || '',
    splitMap: splitMaps?.length > 0,
    ...(layerVisibilityBySplitMap && { layerVisibilityBySplitMap }),
    mapState: {
      latitude: mapState.latitude,
      longitude: mapState.longitude,
      zoom: mapState.zoom
    },
    datasets,
    layers: cleanedLayers as MapConfigForAgent['layers'],
    widgets: parsedWidgets,
    ...(mapSettings && {
      mapSettings: {
        ...(mapSettings.exportPDF && { 'Export PDF': true }),
        ...(mapSettings.addressSearchBar && { 'Search Address': true }),
        ...(mapSettings.basemapsSelector && { 'Change Basemap': true }),
        ...(mapSettings.exportViewportData && { 'Export CSV': true }),
        ...(mapSettings.showMeasureDistanceTool && { 'Measure Distance': true })
      }
    }),
    ...(sqlParameters && {
      parameterControls: sqlParameters
    })
  }
}

/**
 * Checks whether, for a given dataset, we should lazy-load feature properties
 * using the Picking Model API.
 */
export function isAsyncPopupSupported(dataset: Dataset): boolean {
  const state = getStore().getState()

  if (!getFlag(state, FeatureFlag.BuilderPopupAsync)) {
    return false
  }

  const sourceType = getDatasetSourceType(dataset)
  if (sourceType !== MapType.table && sourceType !== MapType.query) {
    return false
  }

  // Picking Model API does not support sources aggregated by geometry or
  // dynamic spatial indexes.
  const isAggregated = !!dataset?.metadata?.aggregationExp
  const isDynamicSpatialIndex = isDynamicSpatialIndexDataset(dataset)
  if (isAggregated || isDynamicSpatialIndex) {
    return false
  }

  // Picking Model API not used in Document Mode.
  // TODO: Remove this case when Document Mode is gone.
  if (!getTilejsonFromTilesetDataset(dataset)) {
    return false
  }

  const schema = dataset.metadata.schema
  if (!schema) {
    return false
  }

  const geoColumn = dataset.metadata.geoColumn || DEFAULT_GEO_COLUMN
  return getSourceSpatialDataType(geoColumn, schema) === SpatialDataType.Geo
}

export function getMetadataFromRasterDataset(dataset: Dataset): RasterMetadata | undefined {
  return dataset.metadata?.raster_metadata
}

export function getBandMetadataFromRasterDataset(dataset: Dataset, band: string): RasterMetadataBand | undefined {
  return dataset.metadata?.raster_metadata?.bands?.find((b) => b.name === band)
}
